import React from "react";
import styled, {useTheme} from "styled-components";
import {ChevronSmallRight} from "styled-icons/entypo/ChevronSmallRight";
import {Pencil} from "styled-icons/heroicons-outline/Pencil";
import {
  ModalActionContainer,
  ModalContainer,
  ModalSection,
  ModalSectionButton,
  ModalText,
  ModalTitle,
} from "../../common/components/modules/modal/modalComponents";
import RowLayout from "../../common/components/elements/container/row";
import useAuthentication from "src/core/authentication/hooks/useAuthentication";
import useRouter from "../../common/hooks/useRouter";
import AuthenticationRequiredComponent from "../../common/components/utils/AuthenticationRequiredComponent";
import routes from "../../common/routes";
import Modal from "../../common/components/modules/modal/Modal";
import AddressSection from "./AddressSection";
import PaymentMethodsSection from "./PaymentMethodsSection";
import useFeatureToggles from "../../common/hooks/useFeatureToggles";
import Link from "../../common/components/modules/Link";
import usePaymentOptions from "../../checkout/hooks/usePaymentOptions";
import LoyaltyHeader from "./LoyaltyHeader";
import {unregisterDevice} from "src/core/api/firebase";
import Title from "src/core/common/components/modules/Title";
import {PaymentOptions} from "src/core/payments/factory";

function MyAccount({isOpen, closeModal}) {
  const [, authApi] = useAuthentication();
  const user = authApi.userProfile();
  const router = useRouter();
  const toggles = useFeatureToggles();
  const theme = useTheme();

  const logout = () => {
    // unregister device for FCM web notifications
    // needs to be done while logged in
    unregisterDevice().finally(() => {
      authApi.logout();
      if (toggles.deliveriesOnly()) {
        router.push(routes.deliveryType);
      } else {
        router.push(routes.home);
      }
    });
  };

  const [paymentOptions] = usePaymentOptions();

  const paymentMethods = {
    stronghold: Boolean(
      paymentOptions.find((po) => po.getType() === PaymentOptions.STRONGHOLD)
    ),
    merrco: Boolean(paymentOptions.find((po) => po.getType() === PaymentOptions.MERRCO)),
    moneris: Boolean(
      paymentOptions.find((po) => po.getType() === PaymentOptions.MONERIS)
    ),
  };

  const hasPaymentMethods = Object.values(paymentMethods).some((val) => val === true);

  return (
    <AuthenticationRequiredComponent
      authenticationFailAction={() => {
        router.safeBack();
      }}
      onRequestCloseLoad={closeModal}
    >
      <Modal isOpen={isOpen} onRequestClose={closeModal}>
        <ModalContainer>
          <Title
            styles={{
              marginBottom: theme.v2.spacing(4),
            }}
          >
            My Account
          </Title>
          <CustomLoyaltyHeader />
          <RowLayout>
            <ModalSection>
              <ModalTitle>Personal information</ModalTitle>
              <ModalSectionButton onClick={() => router.push(routes.personalInfo)}>
                <ModalText>{user && user.getName()}</ModalText>
                <ModalActionContainer>
                  <PencilIcon />
                </ModalActionContainer>
              </ModalSectionButton>
            </ModalSection>
            {toggles.allowDeliveriesInGroup() && (
              <ModalSection>
                <ModalTitle>Delivery Address</ModalTitle>
                <AddressSection
                  editRoute={routes.accountDeliveryCheck}
                  placeholder={"Set delivery address..."}
                  address={user ? user.getDeliveryAddress() : null}
                />
              </ModalSection>
            )}
            {hasPaymentMethods && (
              <ModalSection>
                <ModalTitle>Payment Methods</ModalTitle>
                <PaymentMethodsSection
                  paymentMethods={paymentMethods}
                  editAccountRoute={routes.paymentMethods}
                  editCreditCardsRoute={routes.accountCreditCards}
                />
              </ModalSection>
            )}
            <ModalSectionButton onClick={() => router.push(routes.changePassword)}>
              <ModalText>Change Password</ModalText>
              <ChevronRightIcon />
            </ModalSectionButton>
            <ModalSectionButton onClick={() => router.push(routes.userOrders)}>
              <ModalText>Order history</ModalText>
              <ChevronRightIcon />
            </ModalSectionButton>
            <ModalSectionButton onClick={logout}>
              <ModalText>Logout</ModalText>
              <ChevronRightIcon />
            </ModalSectionButton>
            <DeactivateAccountLink to={{pathname: routes.deactivateAccount}}>
              Deactivate account
            </DeactivateAccountLink>
          </RowLayout>
        </ModalContainer>
      </Modal>
    </AuthenticationRequiredComponent>
  );
}

const CustomLoyaltyHeader = styled(LoyaltyHeader)`
  margin-bottom: ${({theme}) => theme.v2.spacing(8)};
`;

const ChevronRightIcon = styled(ChevronSmallRight)`
  height: 25px;
  width: 25px;
  color: black;
`;

const PencilIcon = styled(Pencil)`
  height: 20px;
  width: 20px;
  color: black;
`;

const DeactivateAccountLink = styled(Link)`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  font-size: ${({theme}) => theme.v2.typography.sizing.s.lg};
  color: ${({theme}) => theme.v2.color.onSurface};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
  &:active {
    color: ${({theme}) => theme.v2.color.onSurface};
  }
`;

export default MyAccount;
